import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const GalleryPage = () => {
  const { t, currentLanguage } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [galleryImages, setGalleryImages] = useState([]);

  const categories = [
    { id: 'all', label: t('gallery.all') },
    { id: 'rooms', label: t('gallery.rooms') },
    { id: 'restaurants', label: t('gallery.restaurants') },
    { id: 'pool', label: t('gallery.pool') },
    { id: 'beach', label: t('gallery.beach') },
    { id: 'spa', label: t('gallery.spa') },
  ];

  const fallbackImages = [
    { url: '/images/rooms/premium_garden.jpg', category: 'rooms', alt_text: { tr: 'Premium Bahçe Odası', en: 'Premium Garden Room' } },
    { url: '/images/rooms/premium_sea.jpg', category: 'rooms', alt_text: { tr: 'Premium Deniz Odası', en: 'Premium Sea Room' } },
    { url: '/images/rooms/premium_swimup.jpg', category: 'rooms', alt_text: { tr: 'Premium Swim-up Oda', en: 'Premium Swim-up Room' } },
    { url: '/images/dining/main_restaurant.jpg', category: 'restaurants', alt_text: { tr: 'Ana Restoran', en: 'Main Restaurant' } },
    { url: '/images/dining/alacarte.jpg', category: 'restaurants', alt_text: { tr: 'A La Carte Restoran', en: 'A La Carte Restaurant' } },
    { url: '/images/dining/lobby_bar.jpg', category: 'restaurants', alt_text: { tr: 'Lobi Bar', en: 'Lobby Bar' } },
    { url: '/images/general/general_3.jpg', category: 'pool', alt_text: { tr: 'Ana Havuz', en: 'Main Pool' } },
    { url: '/images/dining/pool_bar.jpg', category: 'pool', alt_text: { tr: 'Havuz Alanı', en: 'Pool Area' } },
    { url: '/images/general/general_4.jpg', category: 'beach', alt_text: { tr: 'Sahil', en: 'Beach' } },
    { url: '/images/general/general_1.jpg', category: 'beach', alt_text: { tr: 'Sahil Günbatımı', en: 'Beach Sunset' } },
    { url: '/images/general/general_2.jpg', category: 'spa', alt_text: { tr: 'Spa', en: 'Spa' } },
    { url: '/images/rooms/queen_suite.jpg', category: 'spa', alt_text: { tr: 'Fitness', en: 'Fitness' } },
  ];

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/gallery`);
        if (response.data && response.data.length > 0) {
          setGalleryImages(response.data);
        }
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
    };
    fetchGallery();
  }, []);

  const activeImages = galleryImages.length > 0 ? galleryImages : fallbackImages;

  const filteredImages = activeCategory === 'all' 
    ? activeImages 
    : activeImages.filter(img => img.category === activeCategory);

  const getLocalizedText = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[currentLanguage] || obj['tr'] || obj['en'] || '';
  };

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section
        className="relative h-[40vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('/images/general/hero_bg.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-holiday-purple/60" />
        <div className="relative z-10 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">{t('gallery.title')}</h1>
          <p className="text-xl text-white/80">{t('gallery.subtitle')}</p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-white border-b sticky top-0 z-40" data-testid="gallery-filter">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? 'bg-holiday-purple text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-gray-50" data-testid="gallery-grid">
        <div className="container-custom">
          <div className="gallery-grid">
            {filteredImages.map((image, index) => (
              <div
                key={image.id || index}
                className="aspect-[4/3] overflow-hidden rounded-lg cursor-pointer group"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image.url}
                  alt={getLocalizedText(image.alt_text)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>
          <img
            src={selectedImage.url}
            alt={getLocalizedText(selectedImage.alt_text)}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
